import { supabase } from './supabase';

export interface Profile {
  id: string;
  display_name: string | null;
  onboarding_completed: boolean;
  created_at: string;
  updated_at: string;
}

/** Tworzy profil dla nowego użytkownika (signup). Wywołaj gdy user ma aktywną sesję. */
export const createProfileForNewUser = async (
  userId: string,
  displayName?: string | null
): Promise<void> => {
  const { error } = await supabase.from('profiles').insert({
    id: userId,
    display_name: displayName ?? null,
    onboarding_completed: false
  });
  if (error) {
    console.error('Błąd tworzenia profilu:', error);
    // Ignoruj błąd duplikatu (profil mógł już istnieć)
    if (error.code !== '23505') {
      throw error;
    }
  }
};

export const getProfile = async (userId: string): Promise<Profile | null> => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle();

  if (error) {
    console.error('Błąd pobierania profilu:', error);
    return null;
  }
  return data ? { ...data, onboarding_completed: data.onboarding_completed ?? false } : null;
};

/**
 * Pobiera profil lub tworzy go, jeśli nie istnieje.
 * - isExistingUser=true: użytkownik już używał aplikacji, brak profilu -> utwórz z onboarding_completed=true (nie blokuj)
 * - isExistingUser=false: nowy użytkownik -> utwórz z onboarding_completed=false (pokaż onboarding)
 */
export const ensureProfileExists = async (
  userId: string,
  displayName?: string | null,
  isExistingUser = false
): Promise<Profile> => {
  const existing = await getProfile(userId);
  if (existing) return existing;

  const onboardingCompleted = isExistingUser;
  const { data, error } = await supabase
    .from('profiles')
    .upsert(
      {
        id: userId,
        display_name: displayName ?? null,
        onboarding_completed: onboardingCompleted,
        updated_at: new Date().toISOString()
      },
      { onConflict: 'id' }
    )
    .select()
    .single();

  if (error) {
    console.error('Błąd tworzenia profilu:', error);
    throw error;
  }
  return { ...data, onboarding_completed: data?.onboarding_completed ?? onboardingCompleted };
};

export const setOnboardingCompleted = async (userId: string): Promise<void> => {
  const { error } = await supabase
    .from('profiles')
    .upsert(
      {
        id: userId,
        onboarding_completed: true,
        updated_at: new Date().toISOString()
      },
      { onConflict: 'id' }
    );
  if (error) throw error;
};

export const upsertProfile = async (userId: string, displayName: string): Promise<Profile | null> => {
  const { data, error } = await supabase
    .from('profiles')
    .upsert(
      {
        id: userId,
        display_name: displayName.trim() || null,
        updated_at: new Date().toISOString()
      },
      { onConflict: 'id' }
    )
    .select()
    .single();

  if (error) {
    console.error('Błąd zapisywania profilu:', error);
    return null;
  }
  return data;
};
